// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { getDocs, collection } from 'firebase/firestore';
import { db, auth } from '../firebase.js';
import type { Recipe } from '../types/recipe';
import type { Prefs } from '../types/preferences';
import { getAvatarURL } from '../utils/avatar.ts';
import { scaledAmountParts } from '../utils/unitDisplay.ts';
import fetchWithAuth from '../utils/fetchWithAuth.ts';
import BrandSpinner from '../components/ui/atoms/BrandSpinner/BrandSpinner.tsx';
import AddToMealPlanSheet from '../components/MealPlanner/AddToMealPlanSheet.tsx';
import { Button } from '../components/ui/atoms/Button/Button';
import { RatingStars } from '../components/ui/atoms/RatingStars/RatingStars';
import { BookmarkButton } from '../components/ui/atoms/BookmarkButton/BookmarkButton';
import { TextArea } from '../components/ui/atoms/TextArea/TextArea';
import BackButton from '../components/ui/atoms/BackButton.tsx';
import { getCurrentWeekStart } from '../utils/getCurrentWeekStart.ts';
import { fetchMealPlan } from '../utils/mealPlan';
import RecipeTagList from '../components/ui/molecules/RecipeTagList.tsx';
import FixedBottomCTA from '../components/ui/atoms/Button/FixedBottomCTA';
import { FaRegCalendarPlus } from 'react-icons/fa';
import { RecipeMetaInfo } from '../components/ui/molecules/RecipeMetaInfo';
import WarningBanner, {
  WarningType,
} from '../components/ui/atoms/Warning/WarningBanner.tsx';
import { computeWarning } from '../utils/computeWarning';
import NutritionFacts, {
  NutritionData,
} from '../components/ui/molecules/NutritionFacts';
import { useAuth } from '../context/AuthContext';
import { IoDiamond } from 'react-icons/io5';
import ReportModal from '../components/ui/ReportModal';
import { FaFlag } from "react-icons/fa6";
import { useToast } from '../context/ToastContext';

type CurrentUser = {
  uid: string;
};

const RecipeDetail = () => {
  const { id } = useParams();
  const { backendUser } = useAuth() as any;
  const location = useLocation();
  const onboardingStep = location.state?.onboardingStep as number | undefined;
  const navigate = useNavigate();
  const [nutrition, setNutrition] = useState<NutritionData | null>(null);
  const [recipe, setRecipe] = useState<Recipe>();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [servings, setServings] = useState(1);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [userReview, setUserReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [bookmarked, setBookmarked] = useState(false);
  const [contestTitles, setContestTitles] = useState({});
  const [reviewers, setReviewers] = useState({});
  const [activeStep, setActiveStep] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [justBlockedUserId, setJustBlockedUserId] = useState<string | null>(null);
  const { showToast } = useToast() as any;

  const currentUser = auth.currentUser as CurrentUser;
  const currentWeekStart = getCurrentWeekStart();

  const [showProviderModal, setShowProviderModal] = useState(false);
  const [errorMessage] = useState('');
  const [showAllTags, setShowAllTags] = useState(false);
  const [units, setUnits] = useState([]);
  const [prefs, setPrefs] = useState<Prefs>({
    avoided: [],
    allergic: [],
    excludedTagIds: [],
  });
  const [bannerData, setBannerData] = useState<null | {
    type: WarningType;
    reasons: string[];
  }>(null);

  // ── fetch recipe ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await fetchWithAuth(`/api/recipes/${id}`);
        setRecipe(data);
        setBookmarked(data.isBookmarked);
        setCreator(data.user);
        setServings(data.servings);
      } catch (err) {
        console.error(err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // ── fetch per-serving nutrition once recipe is loaded ─────────────────
  useEffect(() => {
    if (!recipe?.id) return;
    fetchWithAuth(`/api/recipes/${recipe.id}/nutrition`)
      .then((data: NutritionData) => {
        setNutrition(data);
      })
      .catch((err) => {
        console.error("Failed to load nutrition facts:", err);
      });
  }, [recipe?.id]);

  // ── if we arrived via onboarding step, scroll to nutrition section ──
  useEffect(() => {
    // Only if they’re on the 5th step (nutrition) of onboarding
    if (onboardingStep === 5 && nutrition) {
      const el = document.getElementById('nutrition-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [onboardingStep, nutrition]);

  // ── fetch user prefs (only once) ───────────────────────────────
  useEffect(() => {
    fetchWithAuth('/api/user/preferences').then((up) => {
      setPrefs({
        avoided: (up.avoidedIngredients || []).map((i: any) =>
          typeof i === 'string' ? i : i.name
        ),
        allergic: (up.allergicIngredients || []).map((i: any) =>
          typeof i === 'string' ? i : i.name
        ),
        excludedTagIds: up.excludedTagIds || [],
      });
    });
  }, []);

  // ── compute banner once both recipe & prefs are loaded ──────────
  useEffect(() => {
    if (recipe && prefs) {
      setBannerData(computeWarning(recipe, prefs));
    }
  }, [recipe, prefs]);

  useEffect(() => {
    if (!recipe) return;

    fetchWithAuth(`/api/units`)
      .then((u) => setUnits(u))
      .catch((err) => {
        console.warn('Could not load units:', err);
        setUnits([]);
      });

    fetchWithAuth(`/api/reviews/recipe/${recipe?.id}`)
      .then((reviews) => {
        setAllReviews(reviews);
        const map: Record<string, any> = {};
        reviews.forEach((r) => {
          map[r.userId] = r.user;
        });
        setReviewers(map);
        const me =
          currentUser?.uid && reviews.find((r) => r.userId === currentUser.uid);
        setUserReview(me ? { ...me, editing: false } : null);
      })
      .catch((err) => {
        console.warn('Could not load reviews:', err);
        setAllReviews([]);
      });
  }, [recipe, currentUser]);

  useEffect(() => {
    const fetchContests = async () => {
      const snap = await getDocs(collection(db, 'contests'));
      const map: Record<string, string> = {};
      snap.docs.forEach((doc) => {
        map[doc.id] = doc.data().title;
      });
      setContestTitles(map);
    };
    fetchContests();
  }, []);

  useEffect(() => {
    fetchWithAuth('/api/user/preferences').then((up) => {
      setPrefs({
        avoided: (up.avoidedIngredients || []).map((i: any) =>
          typeof i === 'string' ? i : i.name
        ),
        allergic: (up.allergicIngredients || []).map((i: any) =>
          typeof i === 'string' ? i : i.name
        ),
        excludedTagIds: up.excludedTagIds || [],
      });
    });
  }, []);

  const toggleBookmark = async () => {
    try {
      if (bookmarked) {
        await fetchWithAuth(`/api/bookmarks`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipeId: recipe?.id }),
        });
        setBookmarked(false);

        setRecipe((r) => {
          if (!r) return r;
          return {
            ...r!,
            isBookmarked: false,
            bookmarkCount: Math.max(0, r.bookmarkCount - 1),
          };
        });
      } else {
        await fetchWithAuth(`/api/bookmarks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ recipeId: recipe?.id }),
        });
        setBookmarked(true);
        setRecipe((r) => {
          if (!r) return r;
          return {
            ...r!,
            isBookmarked: true,
            bookmarkCount: (r.bookmarkCount || 0) + 1,
          };
        });
      }
    } catch (e) {
      console.error('Failed to toggle bookmark', e);
    }
  };

  const submitReview = async () => {
    if (rating === 0) return;
    try {
      await fetchWithAuth(`/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.uid,
          recipeId: recipe?.id,
          rating,
          comment: comment.trim(),
        }),
      });

      const response = await fetchWithAuth(`/api/reviews/recipe/${recipe?.id}`);
      const reviews = await response;
      setAllReviews(reviews);

      const reviewerMap: Record<string, any> = {};
      reviews.forEach((r: any) => {
        reviewerMap[r.userId] = r.user;
      });
      setReviewers(reviewerMap);

      const me = reviews.find((r: any) => r.userId === currentUser.uid);
      setUserReview(me ? { ...me, editing: false } : null);
      setRating(0);
      setComment('');
    } catch (e) {
      console.error('Failed to submit review', e);
    }
  };

  const fetchAndSetMealPlan = async () => {
    const entries = await fetchMealPlan(currentWeekStart);
  };

  if (loading) {
    return (
      <div className="text-center mt-10">
        <BrandSpinner />
      </div>
    );
  }

  if (notFound || !recipe?.id) {
    return (
      <div className="text-center mt-10 text-red-500">
        <BackButton />
        Recipe not found.
      </div>
    );
  }

  const averageRating = allReviews.length
    ? allReviews.reduce((a, r) => a + r.rating, 0) / allReviews.length
    : null;

  const getUnitName = (unitId: string) => {
    const unit = units.find((u: any) => u.id === unitId);
    return unit ? unit.short || unit.name : '';
  };

  return (
    <>
      <BackButton />
      {/* full-bleed image */}
      <div className="w-full relative">
        <img
          src={recipe?.imageUrl || '/no-image.png'}
          alt={recipe?.title}
          className="w-full h-64 object-cover"
        />
      </div>

      {/* page content */}
      <div
        className="max-w-2xl mx-auto px-4 pt-1
                     pb-[calc(env(safe-area-inset-bottom)+10rem)]"
      >
        {/* ── WARNING BANNER ── */}
        {bannerData && (
          <WarningBanner type={bannerData.type} reasons={bannerData.reasons} />
        )}

        <div className="mt-4 mb-2">
          <h1 className="text-2xl font-bold leading-tight break-words mb-2">
            {recipe?.title}
          </h1>
          <div className="flex items-center mb-4">
            {/* creator info on the left */}
            {creator && (
              <Link
                to={`/app/profile/${recipe?.userId}`}
                className="text-orange-600 font-medium flex items-center gap-1"
              >
                <img
                  src={getAvatarURL(creator)}
                  alt="avatar"
                  className="w-5 h-5 rounded-full"
                />
                <span>{creator.name || creator.username || 'User'}</span>
              </Link>
            )}

            {/* spacer */}
            <div className="flex-1" />

            {/* moved-up bookmark button */}
            {currentUser && (
              <BookmarkButton
                bookmarked={bookmarked}
                bookmarkCount={recipe?.bookmarkCount}
                toggleBookmark={toggleBookmark}
              />
            )}
          </div>
        </div>

        {recipe?.description && <p className="mb-2">{recipe?.description}</p>}

        <RecipeMetaInfo
          rating={averageRating}
          reviewCount={allReviews.length}
          duration={recipe.duration}
          ingredientCount={recipe.ingredients.length}
        />

        <RecipeTagList 
          recipeTags={recipe.recipeTags}
          context="detail"
        />

        <div className="flex justify-between items-center mb-4 mt-5">
          <h2 className="font-semibold text-lg">Ingredients</h2>
          <div className="flex items-center gap-2">
            <span>Servings:</span>
            <button
              onClick={() => setServings(Math.max(1, servings - 1))}
              className="px-2 border rounded"
            >
              −
            </button>
            <span>{servings}</span>
            <button
              onClick={() => setServings(servings + 1)}
              className="px-2 border rounded"
            >
              +
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          {(recipe?.ingredients || []).map((ing, idx) => {
            const isSelected = selectedIngredients.includes(idx);

            const unitName = getUnitName(ing.unitId);
            const { amountStr, unitStr } = scaledAmountParts(
              ing.quantity,
              recipe.servings,
              servings,
              unitName
            );

            const unitLower = (unitStr || '').toLowerCase();
            const isQual = unitLower === 'to taste' || unitLower === 'as needed';
            const name = ing.catalog?.nameCanonical || '—';

            return (
              <div
                key={idx}
                className={`p-2 rounded border flex justify-between cursor-pointer ${
                  isSelected ? 'bg-green-50 border-green-400' : 'bg-gray-50'
                }`}
              >
                {/* allow first two columns to grow so "as needed" does not wrap prematurely */}
                <div className="grid grid-cols-[minmax(4rem,auto)_minmax(4rem,auto)_1fr] items-center gap-2 w-full">
                  {isQual ? (
                    <>
                      {/* combine amount + unit into one expanding cell, no ellipsis */}
                      <span className="col-span-2 text-left whitespace-nowrap">
                        {unitStr}
                      </span>
                      <span className="truncate text-right">{name}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-left">{amountStr}</span>
                      <span className="text-left whitespace-nowrap">{unitStr}</span>
                      <span className="truncate text-right">{name}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-sm">
            {errorMessage}
          </div>
        )}

        <h2 className="font-semibold text-lg mb-2 mt-7">Instructions</h2>
        <div>
          {recipe?.instructions?.map((step, i) => {
            const isActive = activeStep === i;
            const isCompleted = activeStep > i;

            return (
              <div
                key={i}
                onClick={() => setActiveStep(i)}
                className={`relative px-4 py-3 pt-4 cursor-pointer transition border rounded mt-6
                                ${isActive ? 'bg-[#FFE0B2] border-[#FF9800] text-[#E65100]' : ''}
                                ${isCompleted ? 'bg-gray-50 text-gray-400' : ''}
                            `}
              >
                <div
                  className="absolute -top-3 left-0 translate-x-1/4 bg-black text-white text-xs font-bold px-3 py-1 leading-tight border-none rounded-none shadow z-10select-none"
                  style={{
                    borderRadius: 0,
                  }}
                >
                  Step {i + 1}
                </div>
                <p>{step}</p>
              </div>
            );
          })}
        </div>
        
        <h2 id="nutrition-section" className="font-semibold text-lg mt-7">Nutrition</h2>
        {( backendUser?.isSubscribed ||  // real premium
           location.pathname.includes('/app/onboarding/recipe') // onboarding
         ) ? (
          <>
            <p className="text-sm text-gray-600">(Per 1 serving)</p>
            {nutrition && (
              <div className="my-2">
                <NutritionFacts
                  data={nutrition}
                  originalServings={recipe.servings}
                  selectedServings={servings}
                />
              </div>
            )}
          </>
        ) : (
          <div className="p-4 mb-6 bg-purple-50 rounded-lg text-center">
            <p className="text-sm text-gray-700">
              Nutritional facts like calories, fat, protein, etc. are available for Premium users
            </p>
            <button
              onClick={() => navigate('/app/subscribe')}
              className="mt-3 inline-flex items-center bg-purple-600 text-white font-medium py-2 px-4 rounded-md hover:bg-purple-700 transition"
            >
              <IoDiamond className="mr-2" /> Try Premium
            </button>
          </div>
        )}

        <h2 className="font-semibold text-lg mb-2 mt-7" id="reviews-section">
          Reviews
        </h2>

        {currentUser &&
          recipe?.userId !== currentUser.uid &&
          (!userReview || userReview.editing) && (
            <div className="mb-4 space-y-2">
              <p>
                {userReview
                  ? 'Changed your mind? Update your review!'
                  : 'How did it turn out? Leave a review!'}
              </p>
              <RatingStars mode="edit" setRating={setRating} rating={rating} />
              <TextArea
                value={comment}
                placeholder="Comment (optional)"
                onChange={setComment}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center justify-between">
                  {/* new “flag” action, left side */}
                  <button
                    onClick={() => setShowReportModal(true)}
                    className="flex items-center gap-1 text-red-600 hover:underline text-sm"
                  >
                    <FaFlag /> Report
                  </button>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button className="h-[30px]" onClick={submitReview}>
                    {userReview ? 'Update' : 'Submit'}
                  </Button>
                  {userReview && userReview.editing && (
                    <Button
                      className="h-[30px]"
                      variant="outline"
                      onClick={() => {
                        setUserReview({ ...userReview, editing: false });
                        setRating(0);
                        setComment('');
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

        <div className="space-y-4">
          {allReviews.length === 0 ? (
            <p className="text-sm text-gray-500">
              {currentUser && recipe?.userId === currentUser.uid
                ? 'No reviews yet.'
                : 'No reviews yet. Be the first to leave one!'}
            </p>
          ) : (
            allReviews.map((r, i) => {
              const reviewer = reviewers[r.userId];
              return (
                <div key={i} className="border p-3 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <Link
                      to={`/app/profile/${r.userId}`}
                      className="flex items-center gap-2 text-sm font-medium"
                    >
                      <img
                        src={getAvatarURL(reviewer)}
                        alt="avatar"
                        className="w-6 h-6 rounded-full"
                      />
                      {reviewer?.name || 'User'}
                    </Link>
                    <RatingStars rating={r.rating} mode="readonly" />
                  </div>
                  {r.comment && (
                    <p className="text-sm text-gray-700 mt-1">{r.comment}</p>
                  )}

                  {currentUser?.uid === r.userId && (
                    <div className="flex gap-4 mt-2 text-sm text-blue-600 justify-end">
                      <Button
                        variant="plain"
                        onClick={() => {
                          setRating(r.rating);
                          setComment(r.comment);
                          setUserReview({ ...r, editing: true });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="plain"
                        onClick={async () => {
                          await fetchWithAuth(`/api/reviews`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              userId: currentUser?.uid,
                              recipeId: recipe?.id,
                            }),
                          });
                          setUserReview(null);
                          setRating(0);
                          setComment('');

                          const response = await fetchWithAuth(
                            `/api/reviews/recipe/${recipe?.id}`
                          );
                          const reviews = await response;
                          setAllReviews(reviews);
                        }}
                        className="text-red-600"
                      >
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <AddToMealPlanSheet
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          recipe={{
            id: recipe.id,
            title: recipe.title,
            imageUrl: recipe.imageUrl,
          }}
          weekStart={currentWeekStart}
          onAdded={fetchAndSetMealPlan}
        />
        {/* FIXED bottom “Add to Meal Plan” CTA */}
        <FixedBottomCTA
          id="add-to-plan-button"
          onClick={() => setPickerOpen(true)}
          icon={<FaRegCalendarPlus className="w-4 h-4" />}
          text="Add to Meal Plan"
        />
      </div>
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        type="RECIPE"
        targetId={recipe!.id}
        onSubmit={({ blockUser, targetId }) => {
          // show toast
          if (blockUser) {
            setJustBlockedUserId(targetId);
            showToast({ message: "Recipe reported — user also blocked", type: "success" });
          } else {
            showToast({ message: "Recipe reported", type: "success" });
          }
          // navigate away so they don't see blocked content
          navigate('/app/home');
        }}
      />
      {/* undo toast action */}
      {justBlockedUserId && (
        <button
          onClick={async () => {
            await fetchWithAuth(`/api/blocks/${justBlockedUserId}`, { method: 'DELETE' });
            showToast({ message: "Unblocked user", type: "info" });
            setJustBlockedUserId(null);
          }}
          className="fixed bottom-24 right-4 bg-gray-800 text-white px-3 py-1 rounded"
        >
          Undo block
        </button>
      )}
    </>
  );
};

export default RecipeDetail;
